import { NgTemplateOutlet } from '@angular/common';
import type { SimpleChanges, TemplateRef } from '@angular/core';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  Input,
  output,
  signal,
  inject,
  input,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { take } from 'rxjs';

import { filterBoolean } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
// eslint-disable-next-line boundaries/element-types -- FIXME
import { LocationSelectors } from '@core/locations-data';
import {
  ButtonComponent,
  IconComponent,
  RtTooltip,
  TruncateComponent,
} from '@core/user-interface-legacy';

@Component({
  selector: 'rt-candidate-work-locations-button',
  imports: [
    ButtonComponent,
    NgTemplateOutlet,
    IconComponent,
    RtTooltip,
    TranslatePipe,
    TruncateComponent,
  ],
  templateUrl: './candidate-work-locations-button.component.html',
  styleUrls: ['./candidate-work-locations-button.component.less'],
  host: {
    '[class.small]': 'small()',
    '[class.force-edit-icon-visible]': 'forceEditIconVisible()',
    '[class.small-text]': 'fontSizeSmall()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CandidateWorkLocationsButtonComponent {
  private readonly i18n = inject(I18nService);
  private readonly store = inject(Store);

  private readonly translations = {
    archived: this.i18n.translate('app.locations.archived').toLowerCase(),
  };

  public readonly locationIds = input.required<number[]>();
  public readonly canEdit = input<boolean>(undefined!);
  public readonly colorTextStrong = input(false, { transform: booleanAttribute });
  public readonly small = input(false, { transform: booleanAttribute });
  public readonly forceEditIconVisible = input(false, { transform: booleanAttribute });
  public readonly fontSizeSmall = input(false, { transform: booleanAttribute });
  @Input() public emptyStateTemplate?: TemplateRef<void>;

  public edit = output<void>();

  public workLocations = signal<string[]>([]);
  public workLocationsTooltip = computed(() => {
    if (!this.workLocations().length) {
      return '';
    }

    return `${this.i18n.translate('app.candidate.placements.work_location', {
      count: this.workLocations().length,
    })}: ${this.workLocations().join(', ')}`;
  });
  public workLocationsTooltipEnabled = computed(() => !!this.workLocations().length);

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.locationIds?.currentValue) {
      this.setWorkLocations();
    }
  }

  private setWorkLocations(): void {
    this.store
      .select(LocationSelectors.byIds(this.locationIds()))
      .pipe(filterBoolean, take(1))
      .subscribe(locations => {
        this.workLocations.set(
          locations.map(location =>
            location.archivedAt
              ? `${location.name} (${this.translations.archived})`
              : location.name,
          ),
        );
      });
  }
}
